/**
 * Centinelas — vigilancia de stock/precio en tiendas.
 *
 * Cada centinela agrupa targets (una tienda por target). El poller del backend
 * los revisa solo; aquí se crean, se prenden/apagan y se pueden disparar a mano.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import {
  Plus, Trash2, RefreshCw, Radar, ExternalLink, Power, Bell, BellOff,
} from "lucide-react";

type TargetState = "unknown" | "in_stock" | "out_of_stock" | "error";

const TARGET_KINDS = [
  "amazon_mx_dp",
  "amazon_mx_search",
  "liverpool_search",
  "page_match",
] as const;

type TargetKind = (typeof TARGET_KINDS)[number];

/** Plantillas de config por tipo — evita adivinar la forma del JSON. */
const KIND_TEMPLATES: Record<TargetKind, string> = {
  amazon_mx_dp: '{"asin":"B0HJ6F8L6V"}',
  amazon_mx_search: '{"query":"nintendo switch 2 zelda","all":["switch 2"],"any":["40th","aniversario"]}',
  liverpool_search: '{"query":"nintendo switch 2 zelda","all":["switch 2"],"any":["40th","aniversario"]}',
  page_match: '{"url":"https://…","inStock":["Agregar al carrito"],"outOfStock":["Agotado"],"reader":false}',
};

interface Target {
  id: string;
  sentinel_id: string;
  label: string;
  kind: TargetKind;
  config: string;
  enabled: number;
  last_state: TargetState;
  last_title: string | null;
  last_price: number | null;
  last_url: string | null;
  last_detail: string | null;
  last_checked_at: number | null;
  consecutive_errors: number;
}

interface Sentinel {
  id: string;
  name: string;
  notes: string;
  enabled: number;
  interval_ms: number;
  cooldown_ms: number;
  notify_telegram: number;
  max_price: number | null;
  last_checked_at: number | null;
  targets: Target[];
}

interface SentinelEvent {
  id: number;
  target_label: string;
  at: number;
  state: TargetState;
  price: number | null;
  title: string | null;
  url: string | null;
  message: string;
  notified: number;
}

const STATE_STYLE: Record<TargetState, { label: string; color: string }> = {
  in_stock: { label: "DISPONIBLE", color: "var(--bw-success, #37f39a)" },
  out_of_stock: { label: "agotado", color: "var(--bw-muted)" },
  error: { label: "error", color: "var(--bw-danger, #ff4d5e)" },
  unknown: { label: "sin datos", color: "var(--bw-muted)" },
};

function money(n: number | null | undefined): string {
  return n == null ? "—" : `$${n.toLocaleString("es-MX")}`;
}

function ago(ts: number | null): string {
  if (!ts) return "nunca";
  const mins = Math.floor((Date.now() - ts) / 60_000);
  if (mins < 1) return "hace un momento";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} d`;
}

export function CentinelasRoute() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newSentinel, setNewSentinel] = useState({ name: "", notes: "", intervalMin: 10 });
  const [addTargetTo, setAddTargetTo] = useState<string | null>(null);
  const [newTarget, setNewTarget] = useState<{ label: string; kind: TargetKind; config: string }>({
    label: "",
    kind: "amazon_mx_dp",
    config: KIND_TEMPLATES.amazon_mx_dp,
  });

  const { data } = useQuery<{ sentinels: Sentinel[] }>({
    queryKey: ["sentinels"],
    queryFn: () => fetch("/api/sentinels").then((r) => r.json()),
    refetchInterval: 30_000,
  });
  const sentinels = data?.sentinels ?? [];

  const { data: eventData } = useQuery<{ events: SentinelEvent[] }>({
    queryKey: ["sentinels", "events"],
    queryFn: () => fetch("/api/sentinels/events?limit=25").then((r) => r.json()),
    refetchInterval: 30_000,
  });
  const events = eventData?.events ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["sentinels"] });

  const createSentinel = useMutation({
    mutationFn: (input: typeof newSentinel) =>
      fetch("/api/sentinels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: input.name,
          notes: input.notes,
          intervalMs: Math.max(1, input.intervalMin) * 60_000,
        }),
      }).then((r) => r.json()),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      setNewSentinel({ name: "", notes: "", intervalMin: 10 });
    },
  });

  const patchSentinel = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      fetch(`/api/sentinels/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then((r) => r.json()),
    onSuccess: invalidate,
  });

  const removeSentinel = useMutation({
    mutationFn: (id: string) => fetch(`/api/sentinels/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const checkNow = useMutation({
    mutationFn: (id: string) => fetch(`/api/sentinels/${id}/check`, { method: "POST" }).then((r) => r.json()),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["sentinels", "events"] });
    },
  });

  const addTarget = useMutation({
    mutationFn: ({ sentinelId, ...body }: { sentinelId: string; label: string; kind: TargetKind; config: string }) =>
      fetch(`/api/sentinels/${sentinelId}/targets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then((r) => r.json()),
    onSuccess: () => {
      invalidate();
      setAddTargetTo(null);
      setNewTarget({ label: "", kind: "amazon_mx_dp", config: KIND_TEMPLATES.amazon_mx_dp });
    },
  });

  const removeTarget = useMutation({
    mutationFn: (targetId: string) => fetch(`/api/sentinels/targets/${targetId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const inputClass =
    "bg-[var(--bw-surface)] border border-[var(--bw-border)] text-[var(--bw-fg)] text-sm font-mono px-3 py-2 w-full focus:border-[var(--bw-accent)] focus:outline-none";

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <CRTHeader text="Centinelas" level={2} />
        <GlyphButton variant="secondary" size="sm" icon={<Plus size={14} />} onClick={() => setShowCreate(true)}>
          Nuevo
        </GlyphButton>
      </div>

      {showCreate && (
        <CorpoCard title="NUEVO CENTINELA" accent>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Nombre</label>
              <input
                value={newSentinel.name}
                onChange={(e) => setNewSentinel((s) => ({ ...s, name: e.target.value }))}
                placeholder="Ej: Switch 2 — Zelda 40th"
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Cada (min)</label>
              <input
                type="number"
                min={1}
                value={newSentinel.intervalMin}
                onChange={(e) => setNewSentinel((s) => ({ ...s, intervalMin: parseInt(e.target.value) || 10 }))}
                className={inputClass}
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Notas</label>
              <input
                value={newSentinel.notes}
                onChange={(e) => setNewSentinel((s) => ({ ...s, notes: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <GlyphButton size="sm" onClick={() => createSentinel.mutate(newSentinel)} disabled={!newSentinel.name.trim()}>
              Crear
            </GlyphButton>
            <GlyphButton variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
              Cancelar
            </GlyphButton>
          </div>
        </CorpoCard>
      )}

      {sentinels.map((s) => {
        const hit = s.targets.some((t) => t.last_state === "in_stock");
        return (
          <CorpoCard key={s.id} accent={hit}>
            <div className="flex items-start gap-3 mb-3">
              <Radar size={18} style={{ color: hit ? "var(--bw-success, #37f39a)" : "var(--bw-accent)" }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-[var(--bw-fg)]">{s.name}</p>
                  <HexBadge variant={s.enabled ? "muted" : "danger"} size="sm">
                    {s.enabled ? `cada ${Math.round(s.interval_ms / 60_000)} min` : "pausado"}
                  </HexBadge>
                  {hit && <HexBadge size="sm">HAY STOCK</HexBadge>}
                </div>
                {s.notes && <p className="text-xs text-[var(--bw-muted)] mt-1">{s.notes}</p>}
                <p className="text-[10px] text-[var(--bw-muted)] mt-1">Último barrido {ago(s.last_checked_at)}</p>
              </div>

              <div className="flex items-center gap-1">
                <button
                  title={s.notify_telegram ? "Avisos por Telegram activos" : "Avisos por Telegram apagados"}
                  onClick={() => patchSentinel.mutate({ id: s.id, notifyTelegram: !s.notify_telegram })}
                  className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-accent)] transition-colors"
                >
                  {s.notify_telegram ? <Bell size={14} /> : <BellOff size={14} />}
                </button>
                <button
                  title={s.enabled ? "Pausar" : "Reanudar"}
                  onClick={() => patchSentinel.mutate({ id: s.id, enabled: !s.enabled })}
                  className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-accent)] transition-colors"
                >
                  <Power size={14} />
                </button>
                <button
                  title="Revisar ahora"
                  onClick={() => checkNow.mutate(s.id)}
                  disabled={checkNow.isPending}
                  className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-accent)] transition-colors disabled:opacity-40"
                >
                  <RefreshCw size={14} className={checkNow.isPending && checkNow.variables === s.id ? "animate-spin" : ""} />
                </button>
                <button
                  title="Borrar centinela"
                  onClick={() => removeSentinel.mutate(s.id)}
                  className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-danger)] transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {s.targets.map((t) => {
                const style = STATE_STYLE[t.last_state];
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-3 border border-[var(--bw-border)] bg-[var(--bw-surface-2)] px-3 py-2"
                  >
                    <span
                      className="w-2 h-2 shrink-0"
                      style={{ backgroundColor: style.color, boxShadow: `0 0 6px ${style.color}` }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-[var(--bw-fg)]">{t.label}</span>
                        <span className="text-[10px] uppercase" style={{ color: style.color }}>
                          {style.label}
                        </span>
                        {t.last_price != null && (
                          <span className="text-xs text-[var(--bw-fg)]">{money(t.last_price)}</span>
                        )}
                      </div>
                      <p className="text-[10px] text-[var(--bw-muted)] truncate">
                        {t.last_detail ?? "sin revisar todavía"} · {ago(t.last_checked_at)}
                      </p>
                    </div>
                    {t.last_url && (
                      <a
                        href={t.last_url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-accent)] transition-colors"
                        title="Abrir en la tienda"
                      >
                        <ExternalLink size={13} />
                      </a>
                    )}
                    <button
                      onClick={() => removeTarget.mutate(t.id)}
                      className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-danger)] transition-colors"
                      title="Quitar target"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>

            {addTargetTo === s.id ? (
              <div className="mt-3 border border-[var(--bw-border)] p-3 space-y-2">
                <div className="grid gap-2 md:grid-cols-2">
                  <input
                    value={newTarget.label}
                    onChange={(e) => setNewTarget((t) => ({ ...t, label: e.target.value }))}
                    placeholder="Etiqueta (ej: Amazon MX)"
                    className={inputClass}
                  />
                  <select
                    value={newTarget.kind}
                    onChange={(e) => {
                      const kind = e.target.value as TargetKind;
                      setNewTarget((t) => ({ ...t, kind, config: KIND_TEMPLATES[kind] }));
                    }}
                    className={inputClass}
                  >
                    {TARGET_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={newTarget.config}
                  onChange={(e) => setNewTarget((t) => ({ ...t, config: e.target.value }))}
                  rows={3}
                  className={`${inputClass} resize-y`}
                />
                <div className="flex gap-2">
                  <GlyphButton
                    size="sm"
                    onClick={() => addTarget.mutate({ sentinelId: s.id, ...newTarget })}
                    disabled={!newTarget.label.trim()}
                  >
                    Agregar target
                  </GlyphButton>
                  <GlyphButton variant="ghost" size="sm" onClick={() => setAddTargetTo(null)}>
                    Cancelar
                  </GlyphButton>
                </div>
              </div>
            ) : (
              <GlyphButton
                variant="ghost"
                size="sm"
                icon={<Plus size={12} />}
                onClick={() => setAddTargetTo(s.id)}
                className="mt-2"
              >
                Target
              </GlyphButton>
            )}
          </CorpoCard>
        );
      })}

      {sentinels.length === 0 && (
        <CorpoCard>
          <div className="text-center py-8">
            <Radar size={48} className="mx-auto text-[var(--bw-muted)] mb-3" />
            <p className="text-sm text-[var(--bw-muted)]">Sin centinelas</p>
            <p className="text-xs text-[var(--bw-muted)] mt-1">Crea uno para vigilar stock o precio</p>
          </div>
        </CorpoCard>
      )}

      <CorpoCard title="HISTORIAL">
        {events.length === 0 ? (
          <p className="text-xs text-[var(--bw-muted)] py-2">Sin eventos todavía.</p>
        ) : (
          <div className="space-y-1">
            {events.map((e) => (
              <div key={e.id} className="flex items-baseline gap-2 text-[11px] font-mono">
                <span className="text-[var(--bw-muted)] shrink-0">
                  {new Date(e.at).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="shrink-0" style={{ color: STATE_STYLE[e.state].color }}>
                  {e.target_label}
                </span>
                <span className="text-[var(--bw-fg)] truncate">
                  {e.message}
                  {e.price != null ? ` · ${money(e.price)}` : ""}
                </span>
                {e.notified === 1 && <Bell size={10} className="text-[var(--bw-accent)] shrink-0" />}
              </div>
            ))}
          </div>
        )}
      </CorpoCard>
    </div>
  );
}
